import { Component, OnInit } from "@angular/core";
import { FormGroup, FormBuilder, Validators } from "@angular/forms";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Router } from "@angular/router";
import { OnboardingService } from "app/services/onboarding.service"; // adjust path if needed

@Component({
  selector: "app-onboarding-form",
  templateUrl: "./onboarding-form.component.html",
  styleUrls: ["./onboarding-form.component.scss"],
})
export class OnboardingFormComponent implements OnInit {
  onboardingForm!: FormGroup;
  loading = false;

  constructor(
    private fb: FormBuilder,
    private onboardingService: OnboardingService,
    private snackBar: MatSnackBar,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.onboardingForm = this.fb.group({
      organizationName: ["", Validators.required],
      organizationType: ["", Validators.required],
      website: [
        "",
        [Validators.pattern(/^(https?:\/\/)?([\w\d-]+\.)+\w{2,}(\/.*)?$/)],
      ],

      fullName: [
        "",
        [Validators.required, Validators.pattern(/^[a-zA-Z\s]+$/)],
      ],
      email: ["", [Validators.required, Validators.email]],
      phone: [
        "",
        [Validators.required, Validators.pattern(/^\+?[0-9]{7,15}$/)],
      ],
      role: ["", Validators.required],

      startDate: ["", [this.futureDateValidator]],
      programCount: ["", [Validators.pattern(/^[0-9]+$/), Validators.min(1)]],
      participantCount: [
        "",
        [Validators.pattern(/^[0-9]+$/), Validators.min(1)],
      ],
      hearAbout: ["", Validators.required],

      confirmAccuracy: [false, Validators.requiredTrue],
      confirmEmailUse: [false, Validators.requiredTrue],
      confirmPolicy: [false, Validators.requiredTrue],

      signature: [
        "",
        [Validators.required, Validators.pattern(/^[a-zA-Z\s]+$/)],
      ],
      date: [{ value: this.formatDate(new Date()), disabled: true }],
    });
  }

  get f() {
    return this.onboardingForm.controls;
  }

  private formatDate(date: Date): string {
    return date.toISOString().split("T")[0];
  }

  onSubmit() {
    if (this.onboardingForm.invalid) {
      this.onboardingForm.markAllAsTouched();
      return;
    }

    this.loading = true;
    const payload = this.onboardingForm.getRawValue();

    this.onboardingService.createOnboardingRecord(payload).subscribe({
      next: (res) => {
        if (res && res.success) {
          const message =
            "Organization onboarding submitted successfully. Check your email for details.";

          this.snackBar.open(message, "Close", {
            duration: 6000,
            horizontalPosition: "center",
            verticalPosition: "top",
            panelClass: ["snackbar-success"],
          });

          this.router.navigate(["/home-landing"]);
        } else if (res.code === "EMAIL_EXISTS") {
          // ⚠️ Email already exists case
          this.snackBar.open(
            "Email already exists in our system. Please try a different email.",
            "Close",
            {
              duration: 4000,
              horizontalPosition: "center",
              verticalPosition: "top",
              panelClass: ["snackbar-error"],
            }
          );
        } else {
          this.snackBar.open(
            "Failed to submit onboarding form. Please try again.",
            "Close",
            {
              duration: 3000,
              horizontalPosition: "center",
              verticalPosition: "top",
              panelClass: ["snackbar-error"],
            }
          );
        }

        this.loading = false;
      },
      error: (err) => {
        console.error("❌ Onboarding submission failed:", err);

        if (err.status === 409 && err.error?.code === "EMAIL_EXISTS") {
          this.snackBar.open(
            "Email already exists in our system. Please try a different email.",
            "Close",
            {
              duration: 4000,
              horizontalPosition: "center",
              verticalPosition: "top",
              panelClass: ["snackbar-error"],
            }
          );
        } else {
          this.snackBar.open(
            "Something went wrong while submitting. Please try again.",
            "Close",
            {
              duration: 3000,
              horizontalPosition: "center",
              verticalPosition: "top",
              panelClass: ["snackbar-error"],
            }
          );
        }

        this.loading = false;
      },
    });
  }

  privacyPolicy() {
    this.router.navigate(["/privacy-policy"]);
  }

  onNumberInput(event: KeyboardEvent): void {
    if (
      !/[0-9]/.test(event.key) &&
      event.key !== "Backspace" &&
      event.key !== "Tab"
    ) {
      event.preventDefault();
    }
  }

  onPhoneInput(event: KeyboardEvent, value: string): void {
    if (event.key === "+" && value.length === 0) {
      return;
    }
    if (
      !/[0-9]/.test(event.key) &&
      event.key !== "Backspace" &&
      event.key !== "Tab"
    ) {
      event.preventDefault();
    }
  }

  onSignatureInput(event: KeyboardEvent): void {
    if (
      !/[a-zA-Z\s]/.test(event.key) &&
      event.key !== "Backspace" &&
      event.key !== "Tab"
    ) {
      event.preventDefault();
    }
  }

  futureDateValidator(control: any) {
    if (!control.value) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const chosen = new Date(control.value);
    chosen.setHours(0, 0, 0, 0);
    return chosen < today ? { pastDate: true } : null;
  }

  minDate = new Date();
}
